import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import styles from '../../styles/MFASetup.module.css';

interface MFASetupProps {
  qrCode?: string;
  secretKey?: string;
}

const MFASetup: React.FC<MFASetupProps> = ({ qrCode: propQrCode, secretKey: propSecretKey }) => {
  const navigate = useNavigate();
  const notificationService = useNotification();
  const [currentStep, setCurrentStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [qrCode, setQrCode] = useState<string | undefined>(propQrCode);
  const [secretKey, setSecretKey] = useState<string | undefined>(propSecretKey);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const hasFetchedSetupRef = useRef(false);

  useEffect(() => {
    const fetchMFASetup = async () => {
      if (hasFetchedSetupRef.current) {
        return;
      }
      
      // Skip if we already have QR code data
      if (qrCode && secretKey) {
        setLoadingSetup(false);
        setCurrentStep(2);
        hasFetchedSetupRef.current = true;
        return;
      }
      
      if (propQrCode && propSecretKey) {
        // If props are provided, use them
        setLoadingSetup(false);
        setCurrentStep(2);
        hasFetchedSetupRef.current = true;
        return;
      }
      
      // Skip if we're past the setup step (step 3 or 4)
      if (currentStep >= 3) {
        setLoadingSetup(false);
        hasFetchedSetupRef.current = true;
        return;
      }

      try {
        setLoadingSetup(true);
        hasFetchedSetupRef.current = true;
        const response = await apiService.setupMFA();
        console.log('MFA Setup Response:', response);
        if (response.status === 'success') {
          const data: any = response.data || {};
          console.log('MFA Setup Data:', data);
          
          // Handle QR code - could be qr_code or qrCode
          const qrCodeValue = data.qr_code || data.qrCode;
          if (qrCodeValue) {
            // Add data URL prefix if not already present
            const qrCodeData = qrCodeValue.startsWith('data:') 
              ? qrCodeValue 
              : `data:image/png;base64,${qrCodeValue}`;
            setQrCode(qrCodeData);
            console.log('QR Code set:', qrCodeData.substring(0, 50) + '...');
          } else {
            console.warn('No QR code in response:', data);
          }
          
          // Handle secret key
          const secretValue = data.secret || data.secret_key || data.secretKey;
          if (secretValue) {
            setSecretKey(secretValue);
          }
          
          setCurrentStep(2); // Move to verification step
        } else {
          notificationService.error(response.message || 'Failed to load MFA setup');
          navigate('/settings');
        }
      } catch (error: any) {
        console.error('Error fetching MFA setup:', error);
        notificationService.error('Failed to load MFA setup');
        navigate('/settings');
      } finally {
        setLoadingSetup(false);
      }
    };

    fetchMFASetup();
  }, [propQrCode, propSecretKey, navigate, notificationService]);

  const steps = [
    { id: 1, title: 'Two-factor authentication', completed: currentStep > 1 },
    { id: 2, title: 'Authentication verification', completed: currentStep > 2, active: currentStep === 2 },
    { id: 3, title: 'Save recovery codes', completed: currentStep > 3, active: currentStep === 3 },
    { id: 4, title: 'Two-factor activated', completed: currentStep > 4, active: currentStep === 4, final: true }
  ];

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call API to verify MFA setup
      const response = await apiService.verifyMFASetup(verificationCode);
      
      if (response.status === 'success') {
        const backupCodes = (response as any)?.backup_codes || (response as any)?.backupCodes || [];
        if (backupCodes.length > 0) {
          setBackupCodes(backupCodes);
          setCurrentStep(3);
          setShowBackupCodes(true);
          notificationService.success('MFA has been successfully enabled!');
        } else {
          setError('Backup codes not received');
        }
      } else {
        setError(response.message || 'Verification failed');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Verification failed';
      setError(errorMessage);
      notificationService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesSaved = () => {
    setCurrentStep(4);
    setShowBackupCodes(false);
    // Redirect after a delay
    setTimeout(() => {
      navigate('/settings');
    }, 2000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show success notification
    }).catch(() => {
      // Show error notification
    });
  };

  const downloadBackupCodes = () => {
    const content = `MFA Backup Codes\n\nSAVE THESE CODES IN A SECURE LOCATION\nEach code can only be used once to access your account if you lose your authenticator device.\n\nGenerated on: ${new Date().toLocaleDateString()}\n\n${backupCodes.join('\n')}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Two-Factor Authentication</h1>
            <p className={styles.subtitle}>Protect your account with an extra layer of security</p>
          </div>
          <div className={styles.securityBadge}>
            <svg className={styles.securityIcon} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
            </svg>
            <span>Enhanced Security Setup</span>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.progressSidebar}>
            <div className={styles.progressSteps}>
              {steps.map((step, index) => (
                <div key={step.id} className={`${styles.stepItem} ${step.completed ? styles.completed : ''} ${step.active ? styles.active : ''} ${step.final ? styles.final : ''}`}>
                  <div className={styles.stepCircle}>
                    {step.completed ? (
                      <svg className={styles.checkIcon} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <div className={`${styles.stepLabel} ${step.final ? styles.last : ''}`}>
                    {step.title}
                  </div>
                  {index < steps.length - 1 && <div className={styles.stepConnector}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.mainContent}>
            {loadingSetup && (
              <div className={styles.loadingSection}>
                <div className={styles.spinner}></div>
                <p>Loading MFA setup...</p>
              </div>
            )}
            {!loadingSetup && currentStep === 2 && (
              <div className={styles.verificationSection}>
                <h2 className={styles.sectionTitle}>
                  <svg className={styles.sectionIcon} fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2 2h2v2H2z"/>
                    <path d="M6 0v6H0V0zM5 1H1v4h4zM4 12H2v2h2z"/>
                    <path d="M6 10v6H0v-6zm-5 1v4h4v-4zm11-9h2v2h-2z"/>
                    <path d="M10 0v6h6V0zm5 1v4h-4V1zM8 1V0h1v2H8v2H7V1zm0 5V4h1v2zM6 8V7h1V6h1v2h1V7h5v1h-4v1H7V8zm0 0v1H2V8H1v1H0V7h3v1zm10 1h-1V7h1zm-1 0h-1v2h2v-1h-1zm-4 0h2v1h-1v1h-1zm2 3v-1h-1v1h-1v1H9v1h3v-2zm0 0h3v1h-2v1h-1zm-4-1v1h1v-2H7v1z"/>
                    <path d="M7 12h1v3h4v1H7zm9 2v2h-3v-1h2v-1z"/>
                  </svg>
                  Authentication verification
                </h2>
                <p className={styles.sectionDescription}>
                  Scan the QR code below with your authenticator app. If you can't use a QR code, 
                  {secretKey && (
                    <span>
                      {' '}enter this text code instead: <code className={styles.secretCode}>{secretKey}</code>
                    </span>
                  )}
                </p>
                
                {error && (
                  <div className={styles.errorMessage}>
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    {error}
                  </div>
                )}
                
                <div className={styles.qrSection}>
                  <div className={styles.qrContainer}>
                    <div className={styles.qrBackground}>
                      {qrCode ? (
                        <img 
                          src={qrCode} 
                          alt="MFA QR Code" 
                          className={styles.qrCode}
                          onError={(e) => {
                            console.error('QR code image failed to load');
                            (e.target as HTMLImageElement).src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgZmlsbD0iI2ZmZiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiMwMDAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlPC90ZXh0Pjwvc3ZnPg==";
                          }}
                        />
                      ) : (
                        <div className={styles.qrPlaceholder}>
                          <svg fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm15 0h-3v2h3v-2zm-3 4h3v2h-3v-2zm-2-4h2v3h-2v-3zm-2-2h2v2h-2v-2zm-2-2h2v2H9v-2zm-2 4h2v2H7v-2z"/>
                          </svg>
                          <p>Loading QR code...</p>
                        </div>
                      )}
                    </div>
                    <div className={styles.qrGlow}></div>
                  </div>
                  <div className={styles.qrInstructions}>
                    <div className={styles.instructionStep}>
                      <svg className={styles.instructionIcon} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                      </svg>
                      <p>Install an authenticator app like Google Authenticator or Microsoft Authenticator</p>
                    </div>
                    <div className={styles.instructionStep}>
                      <svg className={styles.instructionIcon} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                      </svg>
                      <p>Tap the scan option and point your camera at this QR code</p>
                    </div>
                  </div>
                </div>
                
                <div className={styles.verificationForm}>
                  <p className={styles.inputLabel}>Enter the 6-digit code from your authenticator app</p>
                  <form id="verify-form" onSubmit={handleVerificationSubmit}>
                    <div className={styles.codeInputContainer}>
                      <svg className={styles.inputIcon} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 8h2v2H9V8zm0 4h2v2H9v-2zm4-4h2v2h-2V8zm4 0h2v2h-2V8zm-4 4h2v2h-2v-2zm-4 0h2v2H9v-2zm8 0h2v2h-2v-2z"/>
                      </svg>
                      <input
                        type="text"
                        className={styles.verificationInput}
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                          setVerificationCode(value);
                          if (error) setError('');
                        }}
                        maxLength={6}
                        pattern="[0-9]{6}"
                        required
                      />
                    </div>
                  </form>
                </div>
                
                <div className={styles.formActions}>
                  <button 
                    type="button" 
                    className={styles.btnCancel}
                    onClick={() => navigate('/settings')}
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                    </svg>
                    Back to Settings
                  </button>
                  <button 
                    type="submit" 
                    form="verify-form" 
                    className={styles.btnContinue}
                    onClick={(e) => {
                      setLoading(true);
                      handleVerificationSubmit(e);
                    }}
                    disabled={loading || !verificationCode.trim()}
                  >
                    {loading ? (
                      <>
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify & Continue
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 3 && showBackupCodes && (
              <div className={styles.backupCodesModal}>
                <div className={styles.modalContent}>
                  <div className={styles.modalHeader}>
                    <svg className={styles.modalIcon} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                    </svg>
                    <h3>Your Backup Codes</h3>
                  </div>
                  <div className={styles.modalBody}>
                    <div className={styles.backupCodesWarning}>
                      <svg fill="currentColor" viewBox="0 0 24 24">
                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                      </svg>
                      <p><strong>Important:</strong> Save these backup codes in a secure location. They provide access to your account if you lose your authenticator device.</p>
                    </div>
                    <div className={styles.backupCodesList}>
                      {backupCodes.map((code, index) => (
                        <div key={index} className={styles.code}>{code}</div>
                      ))}
                    </div>
                    <div className={styles.backupCodesActions}>
                      <button onClick={downloadBackupCodes} className={styles.btnSecondary}>
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                        Download
                      </button>
                      <button onClick={() => copyToClipboard(backupCodes.join('\n'))} className={styles.btnSecondary}>
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                        Copy All
                      </button>
                      <button onClick={handleBackupCodesSaved} className={styles.btnPrimary}>
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        I've Saved These
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className={styles.successSection}>
                <div className={styles.successIcon}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <h2 className={styles.successTitle}>Two-Factor Authentication Activated!</h2>
                <p className={styles.successDescription}>
                  Your account is now protected with two-factor authentication. 
                  You'll be redirected to the dashboard shortly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
